import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { AcknowledgeQualityManualComponent } from '@aca-new/app/shared/components/acknowledge-quality-manual/acknowledge-quality-manual.component';
import { DialogService } from '@aca-new/app/shared/components/modal/shared/services/dialog.service';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { IAcknowledgeQualityManualResponse } from '@aca-new/app/shared/models/interfaces/quality-manual.interface';
import { QimaNullableType } from '@aca-new/app/shared/models/types/qima.type';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { AppSnackbarService } from '@aca-new/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { Injectable } from '@angular/core';
import { EQimaSnackbarType } from '@qima/ngx-qima';
import { Observable } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AcknowledgeQualityManualService {
  private _isModalShown: boolean = false;

  public constructor(
    private readonly _httpService: HttpService,
    private readonly _dialogService: DialogService,
    private readonly _appSnackbarService: AppSnackbarService
  ) {}

  public init(userData: IUser): void {
    // Exit if no master account
    if (!userData.company?.parentCompanyName) {
      return;
    }

    // Only show modal once per session
    if (this._isModalShown) {
      return;
    }

    const companyId = userData.company.id;

    this._getQualityManualFromMaster$(companyId).subscribe((qualityManualData): void => {
      const content = qualityManualData?.content;

      // Exit if no data or no content
      if (!content) {
        return;
      }

      const isAcknowledged = content.isAcknowledge === '1';

      // Show modal if not yet acknowledged and there is a file
      if (!isAcknowledged && content.filename) {
        this._dialogService.open(AcknowledgeQualityManualComponent, {
          data: {
            onAcknowledge: (companyIdToAck: string): Observable<void> => this.acknowledgeQualityManual$(companyIdToAck),
            qualityManualData: content,
            userData,
          },
          stopCloseWhenClickBackdrop: true,
        });

        this._isModalShown = true;
      }
    });
  }

  public acknowledgeQualityManual$(companyId: string): Observable<void> {
    return this._markAsAcknowledged$(companyId).pipe(
      tap((): void => {
        this._appSnackbarService.showSnackbar('ACKNOWLEDGE_QUALITY_MANUAL.SUCCESS_MESSAGE', EQimaSnackbarType.SUCCESS);
      }),
      catchError((error): Observable<never> => {
        this._appSnackbarService.showSnackbar('ACKNOWLEDGE_QUALITY_MANUAL.ERROR_MESSAGE', EQimaSnackbarType.ERROR);
        throw error;
      }),
      map((): void => undefined)
    );
  }

  private _markAsAcknowledged$(companyId: string): Observable<void> {
    const url = `${SERVER_URL}/company/${companyId}/acknowledge/quality-manual`;
    const body = {
      isAcknowledge: '1',
    };

    return this._httpService.httpClient.put<void>(url, body);
  }

  private _getQualityManualFromMaster$(companyId: string): Observable<QimaNullableType<IAcknowledgeQualityManualResponse>> {
    const url = `${SERVER_URL}/company/${companyId}/quality-manual`;

    return this._httpService.httpClient.get<IAcknowledgeQualityManualResponse>(url);
  }
}
